import React, { useState, useEffect } from 'react';
import { Modal, SearchBar, StatCard } from '../components/common';
import { userService } from '../services/userService';
import { toast } from 'react-hot-toast';
import { TeamsScreen } from './verve-teams/screens/TeamsScreen';

export const UsersPage = () => {
  return <TeamsScreen />;
};

const ROLE_COLORS = {
  admin: 'bg-red-50 text-red-700',
  owner: 'bg-purple-50 text-purple-700',
  sales_executive: 'bg-blue-50 text-blue-700',
  site_manager: 'bg-green-50 text-green-700',
  designer: 'bg-pink-50 text-pink-700',
  'project-manager': 'bg-indigo-50 text-indigo-700',
  inventory_manager: 'bg-yellow-50 text-yellow-700',
  accountant: 'bg-orange-50 text-orange-700',
  field_officer: 'bg-teal-50 text-teal-700',
  telecaller: 'bg-cyan-50 text-cyan-700',
};

export const LegacyUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const roles = [
    // { id: 'admin', label: 'Admin' },
    { id: 'project-manager', label: 'Project Manager' },
    { id: 'field_officer', label: 'Installation member' },
    { id: 'accountant', label: 'Accountant' },
    { id: 'store-manager', label: 'Store Incharge' },
    { id: 'telecaller', label: 'Telecaller' },
    { id: 'designer', label: 'Designer' }
  ];
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', role: roles[0].id, password: '' });
  const [editingUser, setEditingUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await userService.getUsers({ search });
      if (res.success) {
        setUsers(res.data);
        setStats(res.stats);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSubmit = async () => {
    try {
      let res;
      if (editingUser) {
        // Prepare data for update: exclude password if empty
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        res = await userService.updateUser(editingUser._id, updateData);
      } else {
        res = await userService.createUser(form);
      }

      if (res.success) {
        toast.success(editingUser ? 'Member updated successfully' : 'Member added successfully');
        setShowAdd(false);
        setEditingUser(null);
        setForm({ firstName: '', lastName: '', email: '', phone: '', role: roles[0].id, password: '' });
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editingUser ? 'update' : 'add'} member`);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || roles[0].id,
      password: '' // Don't pre-fill password for security
    });
    setShowAdd(true);
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      const res = await userService.updateUser(id, { status: newStatus });
      if (res.success) {
        toast.success(`User marked as ${newStatus}`);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Update failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-4">
        {stats?.roleBreakdown?.slice(0, 4).map((rb) => (
          <StatCard
            key={rb.name}
            title={rb.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            value={rb.value}
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
            color="orange"
          />
        ))}
        {!stats && [1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
      </div>

      <div className="card p-4 flex items-center gap-3">
        <SearchBar value={search} onChange={setSearch} placeholder="Search team members..." />
        <div className="flex-1" />
        <button onClick={() => setShowAdd(true)} className="btn-primary">+ Add Member</button>
      </div>

      {loading && users.length === 0 ? (
        <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-b-2 border-blum-orange rounded-full" /></div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Member', 'Contact', 'Role', 'Status', 'Joined', 'Actions'].map(h => (
                  <th key={h} className={`table-header px-4 py-3 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(user => (
                <tr key={user._id} className={`transition-all duration-300 ${user.status === 'Inactive' ? 'bg-slate-100/80 text-slate-400 select-none' : 'hover:bg-gray-50/50'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${user.status === 'Inactive' ? 'bg-slate-300' : 'bg-gradient-to-br from-orange-400 to-blum-orange'}`}>
                        <span className="text-white text-sm font-bold">{(user.name || user.firstName || '?').charAt(0)}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blum-dark">{user.name || `${user.firstName} ${user.lastName}`}</div>
                        <div className="text-xs text-blum-slate">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-blum-dark">{user.phone || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${ROLE_COLORS[user.role] || 'bg-gray-50 text-gray-700'} capitalize`}>{user.role.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${user.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {user.status === 'Active' ? '● Active' : '● Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-blum-slate">{new Date(user.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => toggleActive(user._id, user.status || 'Active')}
                        className={`p-2 rounded-xl transition-all duration-300 group bg-gray-50/50 ${user.status === 'Active'
                            ? 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                            : 'text-red-500 hover:bg-red-50'
                          }`}
                        title={user.status === 'Active' ? 'Disable User' : 'Enable User'}
                      >
                        {user.status === 'Active' ? (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                            <line x1="18" x2="22" y1="8" y2="12" />
                            <line x1="22" x2="18" y1="8" y2="12" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                            <polyline points="16 11 18 13 22 9" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(user)}
                        disabled={user.status === 'Inactive'}
                        className={`p-2 rounded-xl transition-all duration-300 group bg-gray-50/50 ${user.status === 'Inactive' ? 'opacity-30 cursor-not-allowed text-slate-300' : 'text-slate-400 hover:bg-slate-50 hover:text-blum-orange'}`}
                        title={user.status === 'Inactive' ? 'Cannot edit inactive member' : 'Edit Member'}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-blum-slate">No team members found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showAdd} onClose={() => { setShowAdd(false); setEditingUser(null); }} title={editingUser ? "Edit Team Member" : "Add Team Member"} size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">First Name *</label><input className="input-field" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" /></div>
            <div><label className="label">Last Name</label><input className="input-field" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" /></div>
          </div>
          <div><label className="label">Email *</label><input className="input-field" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@blum.com" /></div>
          <div><label className="label">Phone</label><input className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 9876543210" /></div>
          <div><label className="label">Role *</label>
            <select className="select-field" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
          <div><label className="label">{editingUser ? "New Password (leave blank to keep current)" : "Password *"}</label><input className="input-field" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 chars, uppercase, special" /></div>
          <div className="flex gap-3 pt-2">
            <button className="btn-secondary flex-1" onClick={() => { setShowAdd(false); setEditingUser(null); }}>Cancel</button>
            <button className="btn-primary flex-1" onClick={handleSubmit}>{editingUser ? "Update Member" : "Add Member"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
